import { useState, useRef, useEffect, useId } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp, Check } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface SelectProps {
  label?: string;
  labelIcon?: ReactNode;
  error?: string;
  placeholder?: string;
  options: SelectOption[];
  value?: string;
  onChange?: (e: { target: { value: string } }) => void;
  disabled?: boolean;
  className?: string;
  id?: string;
  /**
   * Tone of the trigger text while nothing is selected.
   *
   * `'value'` (default) renders body copy (#2f2f2f) — the trigger reads as a
   * value slot. `'placeholder'` renders the lighter #7a7a7a that Input and
   * TextArea use.
   *
   * This is a prop rather than one global rule because Figma genuinely
   * specifies both, screen by screen, and QA has verified each:
   *
   *   YAR-159 — invite Access Level → body copy, deliberately DIFFERENT from
   *             the Name/Email inputs sitting directly above it
   *   YAR-289 — profile "Select a role" → placeholder tone, bound to
   *             `text/content/placeholder` in that frame
   *
   * Making either one the single default breaks the other. It was briefly
   * global-placeholder and regressed YAR-159's verified screen within a day.
   */
  placeholderTone?: 'value' | 'placeholder';
}

export function Select({
  label,
  labelIcon,
  error,
  placeholder = 'Select an option',
  options,
  value,
  onChange,
  disabled = false,
  className = '',
  id,
  placeholderTone = 'value',
}: SelectProps) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const selectedOption = options.find((o) => o.value === value);

  const handleSelect = (optValue: string) => {
    onChange?.({ target: { value: optValue } });
    setOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={['flex flex-col gap-2 w-full', className]
        .filter(Boolean)
        .join(' ')}
    >
      {label && (
        <label
          htmlFor={selectId}
          className="text-sm font-medium text-foreground flex items-center gap-1.5"
        >
          {labelIcon}
          {label}
        </label>
      )}

      {/* Trigger */}
      <button
        type="button"
        id={selectId}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${selectId}-error` : undefined}
        className={[
          // The trigger is the same Figma `.field` an Input renders, so it
          // matches one: 40px tall, 1px border, 8px radius (`rounded-lg` is
          // 12px in this theme, not 8px), 12px of horizontal padding.
          //
          // The right padding reserves the chevron's own column exactly as the
          // frame lays it out — 12px padding + 20px icon + 10px gap = 42px.
          'w-full h-10 rounded-md border bg-background text-sm leading-5 text-left',
          'px-3 py-2 pr-[42px] relative',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          'cursor-pointer transition-colors duration-150',
          // Open state is a 1px ring offset 4px from the field. CSS grows an
          // outline's radius by its offset, so 8px + 4px lands on the 12px
          // radius the frame draws, without a second element to position.
          open ? 'outline-1 outline-offset-4 outline-border-strong' : '',
          error
            ? 'border-border-error'
            : open
              ? 'border-border-strong'
              : 'border-border',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* A chosen value is always body copy. The EMPTY state varies by
            screen — see `placeholderTone` on SelectProps for why this cannot
            be one global rule. Default is body copy, which is the tone QA
            verified on the invite screen. */}
        <span
          className={`block truncate pr-2 ${
            selectedOption || placeholderTone === 'value'
              ? 'text-foreground'
              : 'text-text-placeholder'
          }`}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        {/* `icon/neutral/default` (#5e5e5e) — the chevron is an icon, not
            body copy, and was inheriting #2f2f2f (YAR-284). */}
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-icon-default pointer-events-none">
          {open ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </button>

      {/* Menu — flows inline below the trigger, pushing content down */}
      {open && (
        <div
          role="listbox"
          aria-labelledby={selectId}
          // 1px border and an 8px radius, matching the trigger. The `sm - card`
          // shadow is what separates the menu from the page now that the border
          // is hairline rather than 2px.
          //
          // The frame sets 12px between trigger and menu where the rest of the
          // stack (label→trigger, trigger→error) is 8px, so the extra 4px is
          // added here rather than widening the container's gap for everyone.
          className="mt-1 bg-background border border-border rounded-md shadow-card overflow-hidden max-h-80 overflow-y-auto"
        >
          {options
            .filter((o) => !o.disabled)
            .map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option.value)}
                  className={[
                    // 16px horizontal / 12px vertical padding per the frame —
                    // this shipped at 16px on both axes.
                    'w-full flex gap-2.5 items-center px-4 py-3 text-left cursor-pointer',
                    'border-b border-border last:border-b-0',
                    // Selected fill is `surface/neutral/default-subtle`
                    // (#f5f5f5) = `surface-muted`, a shade darker than the
                    // #f9f9f9 `muted` this shipped with.
                    isSelected ? 'bg-surface-muted' : 'hover:bg-muted/50',
                  ].join(' ')}
                >
                  {/* Label and description sit INLINE, per Figma ("Can edit
                      Edit tasks and events"). The original shipped that way
                      too, but with `truncate` on both, which clipped each to a
                      couple of words — QA could read neither ("the options on
                      the Access Level dropdown can't be read, should be shown
                      completely", YAR-159). The fix for that was to stack
                      them, which traded one mismatch for another (YAR-159
                      again: "description of the access level options doesn't
                      match figma").

                      So: inline, and `flex-wrap` instead of `truncate`. A pair
                      that fits reads as one line the way the frame draws it; a
                      pair that doesn't — a long description, a translation —
                      drops the description onto its own line. Either way the
                      full string is on screen, which is the property that
                      actually failed the first time. */}
                  <div className="flex flex-1 flex-wrap items-baseline gap-x-2 gap-y-0.5 min-w-0 text-left">
                    {/* `body-md/medium` — paragraph-md is 16/24, which
                        `text-base` already carries. This shipped at 14px. */}
                    <span className="text-base font-medium text-foreground">
                      {option.label}
                    </span>
                    {/* The description stays at 14px: every menu frame
                        available shows a label ONLY, so its size is unverified,
                        and YAR-159 has already been reversed twice over this
                        pair. Not a value to guess at. */}
                    {option.description && (
                      <span className="min-w-0 text-sm text-muted-foreground">
                        {option.description}
                      </span>
                    )}
                  </div>
                  {isSelected && (
                    <Check
                      size={24}
                      // `icon/neutral/default` #5e5e5e (QA on YAR-159).
                      className="shrink-0 text-icon-default"
                    />
                  )}
                </button>
              );
            })}
        </div>
      )}

      {error && (
        <p
          id={`${selectId}-error`}
          // Error copy is paragraph-sm (14/20), the same ramp Input uses. This
          // shipped at 12px, which matched neither.
          className="text-sm text-text-error leading-5"
        >
          {error}
        </p>
      )}
    </div>
  );
}
